/** @format */

import React, { lazy, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import Loader from '../loader';
import { iconList } from './list-of-all-icons';
export { iconList };
import { loadScrollServices } from '@services/scroll-services';

type IconComponentProps = {
  IconSvg: React.ComponentType;
};

const FullIconList = lazy(
  () => import(/* webpackChunkName: "icon-list" */ './icon-list')
);

/**
 * Load Icon.
 *
 * @param iconClass
 * @returns
 */
export const loadIcon = (
  iconClass: string,
  iconList: Array<string>,
  iconType: string
) => {
  let component = null;
  if (iconClass) {
    iconList.forEach((icon) => {
      if (iconClass.indexOf(icon) > -1) {
        const componentPath = `./${iconType}icon-types/${iconType}${icon}-icon`;
        component = lazy(() =>
          import(/* webpackChunkName: "[request]" */ `${componentPath}`).catch(
            () => console.warn(`Error importing: ${componentPath}`)
          )
        );
      }
    });
  }
  return component;
};

/**
 * Gets Attribute from classlist.
 */

const getIconAttribute = (start, end, string) => {
  const attributeStart = string.indexOf(start);
  let attributeEnd = string.indexOf(end, attributeStart);
  if (attributeEnd === -1) {
    attributeEnd = string.length;
  }

  if (attributeStart > -1) {
    return string.slice(attributeStart + start.length, attributeEnd);
  }
  return null;
};

/**
 *  Icon Component
 *
 * @param IconSvg
 * @returns
 */
const IconComponent: React.FC<IconComponentProps> = ({ IconSvg }) => {
  useEffect(() => {
    loadScrollServices();
  }, []);

  if (IconSvg) {
    return <IconSvg />;
  }
  return null;
};

/**
 * Icon Builder.
 *
 * @param NodeListOf<HTMLElement> icons
 */
export const iconBuilder = (icons: NodeListOf<HTMLElement>): void => {
  icons.forEach((icon) => {
    const name = getIconAttribute('icon-', ' ', icon.className);
    const type = getIconAttribute('type-', ' ', icon.className);
    const IconSvg = loadIcon(name, iconList, type ? type + '-' : '');

    if (IconSvg) {
      const root = createRoot(icon);
      root.render(
        <Suspense fallback={<Loader />}>
          <div className={`loading`}>
            <IconComponent IconSvg={IconSvg} />
          </div>
        </Suspense>
      );
    }
  });
};

/**
 * Icon Setup.
 */
export const icons = (): void => {
  const iconContainer: HTMLElement | null =
    document.querySelector('.icon-list');
  if (iconContainer) {
    const root = createRoot(iconContainer);
    root.render(
      <Suspense fallback={<Loader />}>
        <FullIconList icons={iconList} />
      </Suspense>
    );
  }

  const icons: NodeListOf<HTMLElement> = document.querySelectorAll('.svg-icon');
  if (icons && icons.length > 0) {
    iconBuilder(icons);
  }
};
